<template>
  <v-row>
    <v-col cols="12">
      <v-text-field
          label="辞書名"
          v-model="form.name"
          :rules="[v => !!v || '辞書名が必要です']"
          hint="後から変更が可能です"
          autofocus>
      </v-text-field>
    </v-col>
    <v-col cols="12">
      <v-textarea
          label="説明"
          v-model="form.description"
          messages="複数行入力可"
          placeholder="未入力"
          rows="1"
          auto-grow>
      </v-textarea>
    </v-col>
    <v-col cols="12" v-if="dictionaryType === 'local'">
      <model-select
          v-model="form.defaultDictionaryId"
          :items="() => loadCommonDictionaries()"
          :groups="() => loadCommonDictionaryGroups()"
          label="デフォルトの共通辞書"
          hint="後から変更が可能です"
          :rules="[v => !!v || 'デフォルトの共通辞書が必要です']"
          default-expand-all
          taller>
        <template #item="{item}">
          <div class="select-common-dictionary-item">
            <text-field-like
                :value="item.name"
                :value-hds-id="item.id"
                class="name-field"
                color="inherit"
                hide-details>
            </text-field-like>
            <owner-field
                :value="item.ownerId"
                class="owner-field"
                color="inherit"
                :value-handler="owner => item.ownerName = owner.name || ''"
                hide-details>
            </owner-field>
          </div>
        </template>
      </model-select>
    </v-col>
  </v-row>
</template>

<script>
import ModelSelect from "@/components/molecules/ModelSelect.vue";
import TextFieldLike from "@/components/molecules/TextFieldLike.vue";
import OwnerField from "@/components/molecules/OwnerField.vue";
import query from "@/gql/DictionaryList/Query.gql";
import IdInfo from "@/store/modules/hds/IdInfo";
import mutateDictionaryAdd from "@/gql/DictionaryList/DictionaryAdd.gql";
import {mapActions, mapGetters, mapMutations} from "vuex";
import {DICTIONARY_TYPE_NAMES} from "@/constants";
export default {
  name: "DictionaryCreateFrom",
  components: {OwnerField, TextFieldLike, ModelSelect},
  inject: {
    dialog: {default: null},
  },
  props: {
    dictionaryType: {
      type: String,
      required: true,
    }
  },
  data() {
    return {
      form: {
        name: '',
        description: '',
        defaultDictionaryId: null,
        sourceDictionaryId: null,
        referenceDictionaryIds: [],
      },
      loadCommonDictionariesPromise: null,
      starredDictionaries: [],
      initHandler: () => {
        this.init();
      },
      applyHandler: () => {
        this.apply();
      },
      resetHandler: () => {
        this.reset();
      },
    };
  },
  created() {
    this.dialog.vm.$once('open', this.initHandler);
    this.dialog.vm.$on('apply', this.applyHandler);
    this.dialog.vm.$on('close', this.resetHandler);
  },
  beforeDestroy() {
    this.dialog.vm.$off('open', this.initHandler);
    this.dialog.vm.$off('apply', this.applyHandler);
    this.dialog.vm.$off('close', this.resetHandler);
  },
  computed: {
    ...mapGetters({
      hcsClient: 'hcs/client',
    }),
    dictionaryTypeName() {
      return DICTIONARY_TYPE_NAMES[this.dictionaryType] ?? '辞書';
    },
  },
  methods: {
    ...mapMutations({
      showSnackbar: 'showSnackbar'
    }),
    ...mapActions({
      gqlQuery: 'hds/gqlQuery',
      gqlQueryAndLoad: 'hds/gqlQueryAndLoad',
      gqlMutate: 'hds/gqlMutate',
      gqlMutateAndLoad: 'hds/gqlMutateAndLoad'
    }),
    init() {
      this.loadStarredDictionaries();
    },
    async loadStarredDictionaries() {
      const {data: starredDictionaries} = await this.hcsClient.get('/starred_dictionaries');
      this.starredDictionaries = starredDictionaries.slice();
    },
    loadCommonDictionaries() {
      if(this.loadCommonDictionariesPromise) {
        return this.loadCommonDictionariesPromise;
      }

      return this.loadCommonDictionariesPromise = this.gqlQuery({
        query,
        variables: {
          dictionaryTypes: ['common'],
          workingCopy: false
        }
      }).then(data => {
        const dictionaries = data.dictionary;
        return dictionaries.map(dictionary => {
          const elementId = IdInfo.of(dictionary.id).elementId;
          const isStarred = this.starredDictionaries.some(sd => sd.dictionaryId === elementId);
          return {
            ...dictionary,
            groups: [isStarred ? 'starred' : 'not_starred']
          };
        });
      });
    },
    loadCommonDictionaryGroups() {
      return [{
        id: 'starred',
        order: 1,
        name: 'スター付き',
      }, {
        id: 'not_starred',
        order: 2,
        name: 'その他の辞書',
      }];
    },
    apply() {
      const newEntity = {
        ...this.form,
        dictionaryType: this.dictionaryType,
      };
      this.showSnackbar({
        message: `${this.dictionaryTypeName}「${newEntity.name}」を作成中...`,
        timeout: false,
        loading: true
      });
      this.gqlMutateAndLoad({
        mutation: mutateDictionaryAdd,
        variables: {
          addDictionaries: [newEntity]
        },
        mapping: {
          dictionary: 'dictionary'
        }
      })
      .then(response => {
        this.showSnackbar({
          message: `${this.dictionaryTypeName}「${newEntity.name}」を作成しました`
        });
        this.$router.push({
          name: `/edit/${this.dictionaryType}/dictionary/Dictionary`,
          params: {
            dictionaryId: response.dictionary[0].id,
          }
        });
      }, e => {
        console.error(e);
        this.showSnackbar({
          color: 'failed',
          message: `${this.dictionaryTypeName}「${newEntity.name}」の作成に失敗しました`
        });
      });
    },
    reset() {
      this.form = {
        name: '',
        description: '',
        defaultDictionaryId: null,
        sourceDictionaryId: null,
        referenceDictionaryIds: [],
      };
    }
  }
}
</script>

<style lang="scss" scoped>
.select-common-dictionary-item {
  display: flex;
  width: 100%;

  ::v-deep .v-input--is-readonly .v-input__slot {
    cursor: pointer !important;
  }

  & > * {
    flex-grow: 0;
    position: relative;
    top: -8px;
  }

  .name-field {
    flex-grow: 1;
  }

  .owner-field {
    max-width: 200px;
    font-size: 80%;
    color: rgba(0,0,0,.6);
  }
}
</style>
